import { type AttachmentId, pluralize } from "@inrev/common";
import { useFetchAccountAttachments } from "@portal/api/account";
import { type FC, useMemo } from "react";
import { Text } from "../../../../primitives";
import { useDecisioningPanel } from "../DecisioningPanelContext";
import { attachmentFileName } from "./resolveDraft";

export type DataNeededFulfillmentProps = {
	attachmentIds: readonly AttachmentId[];
	answer: string | null;
	testId?: string;
};

const Files: FC<{ attachmentIds: readonly AttachmentId[] }> = ({ attachmentIds }) => {
	const { accountId } = useDecisioningPanel();
	const { attachments } = useFetchAccountAttachments(accountId);
	const names = useMemo(() => {
		const nameById = new Map(
			attachments.map((attachment) => [attachment.id, attachmentFileName(attachment)]),
		);
		const known = attachmentIds.flatMap((id) => nameById.get(id) ?? []);
		const unknown = attachmentIds.length - known.length;
		return unknown > 0 ? [...known, pluralize(unknown, "other file", "other files")] : known;
	}, [attachments, attachmentIds]);

	return (
		<Text type="caption" color="secondary" className="block break-words [text-box-trim:none]">
			{names.join(", ")}
		</Text>
	);
};

/** What satisfied a received request: the answer and the files it was resolved with. */
export const DataNeededFulfillment: FC<DataNeededFulfillmentProps> = ({
	attachmentIds,
	answer,
	testId,
}) => {
	if (attachmentIds.length === 0 && answer === null) return null;
	return (
		<div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
			{answer !== null && (
				<Text
					type="caption"
					color="secondary"
					className="block whitespace-pre-wrap break-words [text-box-trim:none]"
				>
					{answer}
				</Text>
			)}
			{attachmentIds.length > 0 && <Files attachmentIds={attachmentIds} />}
		</div>
	);
};
